import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSEO } from '@/hooks/useSEO';

export default function MockSurveyService() {
  useSEO({
    title: 'ACHC Mock Survey Services for Home Health Agencies | Advantixx',
    description: 'Comprehensive mock survey services that simulate the ACHC accreditation survey experience — identify gaps, prepare staff, and ensure survey readiness with a 100% pass rate.',
    keywords: 'ACHC mock survey, home health mock survey, survey readiness assessment, ACHC survey preparation',
    canonical: '/services/mock-survey',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-mock-survey-svc';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'ACHC Mock Survey Services for Home Health Agencies',
      description: 'Comprehensive mock survey services that simulate the ACHC accreditation survey experience — identify gaps, prepare staff, and ensure survey readiness.',
      provider: { '@type': 'Organization', name: 'Advantixx Home Health Accreditation Consultants' },
      serviceType: 'Mock Survey Consulting',
      areaServed: { '@type': 'Country', name: 'United States' },
    });
    document.head.appendChild(script);
    return () => { document.getElementById('schema-mock-survey-svc')?.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-[#0d0d0d] font-['Montserrat',sans-serif]">
      <Navbar />

      <section className="relative pt-36 pb-0">
        <div className="relative h-[400px] md:h-[480px] overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/fa81205d4c06ac7516264b59b1548eb0.jpg"
            alt="ACHC Mock Survey Services for Home Health Agencies"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-[#0d0d0d]/50 to-[#0d0d0d]/20"></div>
          <div className="absolute bottom-0 left-0 right-0 max-w-5xl mx-auto px-6 md:px-10 pb-12">
            <span className="inline-block bg-[#d4af37] text-[#0d0d0d] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide mb-4">Core Service</span>
            <h1 className="text-3xl md:text-5xl font-black text-white leading-tight">
              ACHC Mock Survey<br className="hidden md:block" />
              <span className="text-[#d4af37]"> &amp; Readiness Assessment</span>
            </h1>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-10 py-16">
        <div className="flex flex-col lg:flex-row gap-12">
          <div className="flex-1 min-w-0">
            <p className="text-[#bbb] text-lg leading-relaxed mb-8 border-l-4 border-[#d4af37] pl-6">
              A mock survey is the single most effective tool for ensuring your agency is ready for the actual ACHC survey. Our mock surveys are conducted with the same rigor and methodology as a real ACHC survey — so there are no surprises on survey day.
            </p>

            <h2 className="text-2xl font-black text-white mb-6 mt-12"><span className="text-[#d4af37]">What Our Mock Survey Includes</span></h2>
            <div className="flex flex-col gap-5 mb-12">
              {[
                { icon: 'ri-file-search-line', title: 'Document & Record Review', desc: 'Comprehensive review of all clinical records, personnel files, policies and procedures, QAPI documentation, training records, and administrative files against ACHC standards.' },
                { icon: 'ri-user-voice-line', title: 'Staff Interviews', desc: 'Structured interviews with clinical staff, administrative personnel, and management to assess knowledge of ACHC standards, agency policies, and their specific roles.' },
                { icon: 'ri-eye-line', title: 'Operational Observation', desc: 'Direct observation of clinical and administrative operations to verify that actual practice aligns with written policies and ACHC standards.' },
                { icon: 'ri-home-heart-line', title: 'Patient Home Visits (Optional)', desc: 'Simulated patient home visits to assess the quality of care delivery, documentation practices, and patient rights compliance in the field.' },
                { icon: 'ri-file-chart-line', title: 'Detailed Findings Report', desc: 'A comprehensive written report documenting all findings, organized by ACHC standard, with specific citations, severity ratings, and prioritized recommendations.' },
                { icon: 'ri-presentation-line', title: 'Debrief & Action Planning', desc: 'A thorough debrief session with leadership to review findings, answer questions, and develop a prioritized action plan for addressing all identified deficiencies.' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 bg-[#141414] border border-[#d4af37]/10 rounded-xl p-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#d4af37]/10 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-[#d4af37] text-lg`}></i>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-sm mb-2">{item.title}</h3>
                    <p className="text-[#777] text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <h2 className="text-2xl font-black text-white mb-6 mt-12"><span className="text-[#d4af37]">When to Schedule a Mock Survey</span></h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-12">
              {[
                { icon: 'ri-time-line', title: '60–90 Days Before Survey', desc: 'The ideal time for a first-time applicant — enough time to address all findings before the actual survey.' },
                { icon: 'ri-refresh-line', title: 'Annual Readiness Check', desc: 'For accredited agencies, an annual mock survey ensures continuous compliance between accreditation cycles.' },
                { icon: 'ri-alert-line', title: 'After a Complaint Survey', desc: 'If your agency has received a complaint survey or citation, a mock survey helps identify systemic issues and prevent recurrence.' },
              ].map((item, i) => (
                <div key={i} className="bg-[#141414] border border-[#d4af37]/15 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#d4af37]/10 rounded-full mx-auto mb-4">
                    <i className={`${item.icon} text-[#d4af37] text-xl`}></i>
                  </div>
                  <h3 className="text-white font-bold text-sm mb-2">{item.title}</h3>
                  <p className="text-[#777] text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-br from-[#d4af37]/10 to-[#d4af37]/5 border border-[#d4af37]/30 rounded-xl p-8 text-center">
              <h3 className="text-white font-black text-xl mb-3">Schedule Your Mock Survey Today</h3>
              <p className="text-[#888] text-sm mb-6 leading-relaxed">Don&apos;t go into your ACHC survey unprepared. Our mock surveys give you the confidence and clarity to pass on the first attempt.</p>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#d4af37] text-[#0d0d0d] font-bold text-sm px-8 py-3.5 rounded-full hover:bg-[#f4c430] transition-colors duration-200 whitespace-nowrap cursor-pointer">
                Book a Mock Survey <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>

          <aside className="lg:w-72 flex-shrink-0">
            <div className="sticky top-28 flex flex-col gap-6">
              <div className="bg-[#141414] border border-[#d4af37]/15 rounded-xl p-6">
                <p className="text-[#d4af37] text-xs uppercase tracking-widest font-semibold mb-5">All Services</p>
                <ul className="flex flex-col gap-3">
                  {[
                    { label: 'Accreditation Preparation', path: '/services/achc-accreditation-preparation' },
                    { label: 'Mock Survey Services', path: '/services/mock-survey', active: true },
                    { label: 'Policy & Procedure Development', path: '/services/policy-procedure-development' },
                    { label: 'Compliance Audits', path: '/services/compliance-audit' },
                    { label: 'Staff Training', path: '/services/staff-training' },
                    { label: 'Ongoing Compliance Support', path: '/services/ongoing-compliance-support' },
                  ].map((item, i) => (
                    <li key={i}>
                      <Link to={item.path} className={`flex items-center gap-2 text-sm transition-colors duration-200 cursor-pointer ${item.active ? 'text-[#d4af37] font-semibold' : 'text-[#888] hover:text-[#d4af37]'}`}>
                        <i className={`ri-arrow-right-s-line ${item.active ? 'text-[#d4af37]' : 'text-[#555]'}`}></i>
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-gradient-to-br from-[#d4af37]/15 to-[#d4af37]/5 border border-[#d4af37]/30 rounded-xl p-6 text-center">
                <i className="ri-phone-line text-[#d4af37] text-3xl mb-3 block"></i>
                <p className="text-white font-bold text-sm mb-2">Speak with Dr. Villarreal</p>
                <p className="text-[#777] text-xs mb-4 leading-relaxed">Get expert answers to your accreditation questions today.</p>
                <Link to="/contact" className="block bg-[#d4af37] text-[#0d0d0d] font-bold text-xs px-5 py-2.5 rounded-full hover:bg-[#f4c430] transition-colors duration-200 whitespace-nowrap cursor-pointer">Contact Us</Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
      <Footer />
    </div>
  );
}
